/**
 * FeeBreakdown Component
 * Renders the opening fee breakdown shared by Offer/Application/Credit detail pages.
 *
 * Siempre muestra los 4 componentes del calculo (monto solicitado, comision,
 * IVA comision, monto financiado) — incluso cuando la comision es 0, porque
 * saber que esta en cero tambien es informacion util.
 */

import { Badge } from '@/components/ui/Badge';
import { formatCurrency } from '@/utils/formatters';

interface FeeBreakdownProps {
  requestedAmountMxn: number;
  openingFeeMxn?: number | undefined;
  openingFeeIvaMxn?: number | undefined;
  openingFeePct?: number | undefined;
  financedAmountMxn?: number | undefined;
}

export function FeeBreakdown({
  requestedAmountMxn,
  openingFeeMxn,
  openingFeeIvaMxn,
  openingFeePct,
  financedAmountMxn,
}: FeeBreakdownProps) {
  const fee = openingFeeMxn ?? 0;
  const feeIva = openingFeeIvaMxn ?? 0;
  const pctLabel = openingFeePct != null ? ` (${(openingFeePct * 100).toFixed(2)}%)` : '';
  const financed = financedAmountMxn ?? requestedAmountMxn + fee + feeIva;
  const hasFee = fee > 0;

  return (
    <div>
      <Row
        label="Monto solicitado"
        value={formatCurrency(requestedAmountMxn, 'MXN', 'es-MX')}
      />
      <Row
        label={`Comision apertura${pctLabel}`}
        value={hasFee ? `+${formatCurrency(fee, 'MXN', 'es-MX')}` : formatCurrency(0, 'MXN', 'es-MX')}
      />
      <Row
        label="IVA comision"
        value={hasFee ? `+${formatCurrency(feeIva, 'MXN', 'es-MX')}` : formatCurrency(0, 'MXN', 'es-MX')}
      />
      <div className="mt-1 flex items-center justify-between border-t border-border pt-1.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">Monto financiado</span>
          {hasFee && <Badge variant="info">Financiada</Badge>}
        </div>
        <span className="text-sm font-bold tabular-nums text-foreground">
          {formatCurrency(financed, 'MXN', 'es-MX')}
        </span>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium tabular-nums text-foreground">{value}</span>
    </div>
  );
}
